import {z} from 'zod';

export const NativeTokenReconnectSchema = z.object({reason: z.literal('service-restart')}).strict();
import type {TeamRoleId} from './team.js';
import type {ChatAttachment} from './chat-attachments.js';
import type {NativeCommunicationStream, NativeCommunicationReceipt} from './native-communication.js';
import type {NativeCommunicationState} from './native-communication.js';
import type {NativePendingConnection} from './native-connection.js';

const tokenCount = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const costEstimate = z.discriminatedUnion('status', [
    z.object({status: z.literal('ready'), costUsd: z.number().finite().nonnegative(), rateDate: z.iso.date(),
      source: z.url(), method: z.literal('standard-token-rates')}).strict(),
    z.object({status: z.literal('error'), error: z.object({name: z.string(), message: z.string(), stack: z.string()}).strict()}).strict(),
  ]);
export const NativeTokenSnapshotSchema = z.object({
  sessionId: z.string().min(1), observedAt: z.iso.datetime(), sequence: tokenCount, epoch: z.string().min(1),
  scope: z.literal('conversation'), source: z.literal('provider-reported'),
  costEstimate,
  agents: z.array(z.object({sessionId: z.string().min(1), inputTokens: tokenCount, outputTokens: tokenCount,
    totalTokens: tokenCount, apiCalls: tokenCount, completeness: z.literal('unknown'),
    costUsd: z.number().finite().nonnegative().nullable(), costState: z.enum(['priced', 'unpriced', 'incomplete']), costEstimate}).strict()
    .refine(value => (value.costState === 'priced') === (value.costUsd !== null), 'Recorded cost and its coverage state disagree.')
    .refine(value => value.totalTokens === value.inputTokens + value.outputTokens,
    'Reported input and output tokens do not match the total.'))
}).strict().refine(value => new Set(value.agents.map(agent => agent.sessionId)).size === value.agents.length,
  'Token totals repeat a native conversation.');
export type NativeTokenSnapshot = z.infer<typeof NativeTokenSnapshotSchema>;

/** Transport views are derived from the account's native Hermes records. */
export interface NativeSessionMapping {
  sessionId: string;
  runId: null;
  kind: 'head';
  createdAt: string;
  /** Native title; null when untitled or the account runtime is stopped. */
  title: string | null;
  /** Latest visible native Head message; null when none is saved or metadata is not loaded. */
  preview: string | null;
  /** Native saved activity/message time; null when metadata is not loaded. */
  updatedAt: string | null;
}

export interface NativeMessage {
  role: string;
  content?: unknown;
  text?: string;
  timestamp?: string | number | null;
  created_at?: string | number | null;
  tool_calls?: unknown;
  tool_call_id?: string | null;
  name?: string | null;
  attachments?: ChatAttachment[];
  communication?: {producer:'user'|'communication'|'work';requestId:string;turnId:string;replyTo?:string;callId?:string;
    agentId?:string|null;resultControlId?:string;incomplete?:boolean};
  [field: string]: unknown;
}

/** Safe execution evidence, derived from native Hermes records rather than a second transcript. */
export interface NativeChatToolActivity {
  id: string;
  name: string;
  kind: 'command' | 'tool';
  label: string;
  status: 'running' | 'completed' | 'failed' | 'unknown';
  startedAt: string | null;
  finishedAt: string | null;
  detail: string | null;
}
export interface NativeChatFileChange {
  path: string;
  additions: number | null;
  deletions: number | null;
  diff: string | null;
  diffTruncated: boolean;
}
export interface NativeChatDelegation {
  id: string;
  sessionId: string | null;
  roleId: string | null;
  status: string;
  startedAt: string;
  finishedAt: string | null;
}
export interface NativeChatTurnActivity {
  id: string;
  userRowId: number | null;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'completed' | 'interrupted' | 'failed' | 'unknown';
  tools: NativeChatToolActivity[];
  files: NativeChatFileChange[];
  agents: NativeChatDelegation[];
  detailsTruncated: boolean;
}
export interface NativeChatActivity {
  available: boolean;
  reason: string | null;
  observedAt: string;
  turns: NativeChatTurnActivity[];
  truncated: boolean;
}

export interface NativeTaskUsage {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  observedAt: string;
  status: 'running' | 'completed' | 'interrupted' | 'failed';
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number | null;
  pricingState: 'priced' | 'unpriced' | 'unattributed';
  unpricedEvents: number;
  unattributedEvents: number;
  eventCount: number;
}

export interface NativeHourlyUsage {
  windowStart: string;
  windowEnd: string;
  inputTokens: number;
  outputTokens: number;
  hourlyTokens: number;
  hourlyUsd: number | null;
  pricingState: 'priced' | 'unpriced';
  unpricedEvents: number;
  eventCount: number;
  task: NativeTaskUsage | null;
}

export const NativeTodoStateSchema = z.object({revision: z.number().int().nonnegative(), todos: z.array(z.object({
  id: z.string(), content: z.string(), status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']), parent: z.string().optional()
}))});
export type NativeTodoState = z.infer<typeof NativeTodoStateSchema>;

export interface NativeExecutionObservation {
  currentTool: string | null;
  activity: string | null;
  activityAt: string | null;
  observedAt: string;
  endedAt: string | number | null;
  /** Unfinished native async execution or completion delivery owned by this conversation. */
  pendingDelegations: Array<{id: string; state: string; deliveryState: string}>;
  /** Native retained turn error when available; lifecycleStatus can also report saved errors. */
  error: string | null;
}

export interface NativeAgentView extends NativeExecutionObservation {
  /** A fixed team identity from native session metadata; null for older or scheduled work. */
  roleId: TeamRoleId | null;
  title: string | null;
  agentId: string;
  sessionId: string;
  parentSessionId: string | null;
  goal: string;
  assignedTask: string | null;
  todoState: NativeTodoState | null;
  status: string;
  running: boolean;
  /** Native continuation claim; it can remain set while the agent is running. */
  resuming: boolean;
  executionKind: 'scheduled' | 'delegated';
  /** Saved transcript status, independent of live execution; null only for a live-only view. */
  lifecycleStatus: 'empty' | 'interrupted' | 'error' | 'complete' | null;
  endReason: string | null;
  acceptingSteer: boolean;
  model: string | null;
  startedAt: string | number | null;
  lastTool: string | null;
  usage: NativeHourlyUsage;
  /** Included when the work details are open; projected from this agent's native tool receipts. */
  chatActivity?: NativeChatActivity;
}

/** Native session.resume snapshot; saved messages remain in history. */
export const NativeLiveTurnSchema = z.object({
  assistant: z.string(), streaming: z.boolean(), user: z.string(), started_at: z.number().positive(),
  user_row_id: z.number().int().positive().optional(),
  corrections: z.array(z.string()).optional(), correction_offsets: z.array(z.number().int().nonnegative()).optional(),
  display_kind: z.string().optional(), display_metadata: z.record(z.string(), z.unknown()).optional(),
  error: z.string().optional(), status: z.string().optional(), recoverable: z.boolean().optional(),
  error_surface: z.record(z.string(), z.unknown()).optional()
}).strip();

export const NativeHistoryRequestSchema = z.object({
  before: z.string().regex(/^(?:logical:[a-f0-9]{64}|row:[1-9][0-9]{0,18})$/).optional(),
  after: z.string().regex(/^(?:logical:[a-f0-9]{64}|row:[1-9][0-9]{0,18})$/).optional()
}).strict().refine(value => value.before === undefined || value.after === undefined, 'Select one history direction.');
export type NativeHistoryRequest = z.infer<typeof NativeHistoryRequestSchema>;
export const NativeHistoryPageSchema = z.object({
  before: z.string().nullable(), after: z.string().nullable(), hasOlder: z.boolean(), hasNewer: z.boolean(),
  bytes: z.number().int().nonnegative().max(1000000), limitBytes: z.literal(1000000)
}).strict();
export type NativeHistoryPage = z.infer<typeof NativeHistoryPageSchema>;

export interface NativeSessionDetail {
  session: NativeSessionMapping;
  /** Hermes's display projection of the same saved history, including native steer and skill text. */
  displayHistory: NativeMessage[];
  historyPage: NativeHistoryPage;
  liveTurn: z.infer<typeof NativeLiveTurnSchema> | null;
  /** Optional while the host and account runtime roll forward independently. */
  chatActivity?: NativeChatActivity;
  agents: NativeAgentView[];
  todoState: NativeTodoState | null;
  /** Same response as the agent observations; event replay is not a second current-state source. */
  openRequests: Array<Record<string, unknown>>;
  pendingConnections: NativePendingConnection[];
  observedAt: string;
  state: NativeExecutionObservation & {running: boolean; resuming: boolean; lifecycleStatus: NativeAgentView['lifecycleStatus'];
    status: string; assignedTask: string | null; startedAt: string | number | null;
    endReason: string | null; model: string | null; liveSessionId: string; usage: NativeHourlyUsage};
}

export interface NativeAgentDetail {
  agent: NativeAgentView;
  /** Hermes's display projection of this child's saved history. */
  displayHistory: NativeMessage[];
  historyPage: NativeHistoryPage;
  chatActivity?: NativeChatActivity;
  liveTurn: z.infer<typeof NativeLiveTurnSchema> | null;
  pendingConnections: NativePendingConnection[];
  state: {running: boolean; resuming: boolean};
}

export type NativeConversationPage = Pick<NativeSessionDetail, 'session' | 'displayHistory' | 'historyPage'>;

export interface NativeEventBatch {
  events: Array<Record<string, unknown>>;
  cursor: number;
  epoch: string;
  resetRequired: boolean;
  openRequests: Array<Record<string, unknown>>;
  pendingConnections: NativePendingConnection[];
}

export interface NativeCommunicationOutput {
  sessionId:string;
  session:NativeSessionMapping;
  messagePrefix:string;
  displayHistory:NativeMessage[];
  historyPage:NativeHistoryPage;
  streams:NativeCommunicationStream[];
  receipts:NativeCommunicationReceipt[];
  receiptsBefore:string|null;
  observedAt:string;
}

export interface NativeMessageResult {
  status: string;
  native: Record<string, unknown>;
  /** Native acceptance evidence; absent on non-message control responses. */
  receipt?: {
    requestId: string;
    sessionId: string;
    agentId: string | null;
    observedAt: string;
    message: NativeMessage | null;
    liveTurn: z.infer<typeof NativeLiveTurnSchema> | null;
    communication?: {turnId:string;state:NativeCommunicationState;error?:{message:string;stack?:string}};
  };
}
export interface NativeTerminalTicket {
  webSocketUrl: string;
  ticket: string;
  expiresAt: string;
}
